import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Calendar,
  Plus,
  Users,
  Search,
  Edit,
  Trash2,
  Copy,
  Eye,
  MoreVertical,
  Archive,
  Send,
  QrCode,
  DollarSign,
  Clock,
  MapPin,
  UserX,
  UserCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function AdminEventosPage() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/eventos/admin/lista`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventos(res.data);
    } catch (err) {
      console.error('Error cargando eventos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (eventoId, nuevoEstado) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/eventos/admin/${eventoId}/estado?estado=${nuevoEstado}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEventos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDuplicar = async (eventoId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/eventos/admin/${eventoId}/duplicar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/admin/eventos/${res.data.id}/editar`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al duplicar');
    }
  };

  const handleEliminar = async (eventoId, nombre) => {
    if (!window.confirm(`¿Eliminar el evento "${nombre}"? Esta acción no se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/eventos/admin/${eventoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEventos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleInscripciones = async (eventoId, inscripcionesAbiertas) => {
    const accion = inscripcionesAbiertas ? 'cerrar' : 'abrir';
    if (!window.confirm(`¿Deseas ${accion} las inscripciones para este evento?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/eventos/admin/${eventoId}`, 
        { inscripciones_abiertas: !inscripcionesAbiertas },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEventos();
    } catch (err) {
      alert(err.response?.data?.detail || `Error al ${accion} inscripciones`);
    }
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      borrador: 'bg-gray-100 text-gray-700',
      publicado: 'bg-green-100 text-green-700',
      archivado: 'bg-amber-100 text-amber-700'
    };
    const labels = {
      borrador: 'Borrador',
      publicado: 'Publicado',
      archivado: 'Archivado'
    };
    return <Badge className={estilos[estado] || estilos.borrador}>{labels[estado] || estado}</Badge>;
  };

  const eventosFiltrados = eventos.filter(e => {
    const matchSearch = e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       e.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filtroEstado === 'todos' || e.estado === filtroEstado;
    return matchSearch && matchEstado;
  });

  // Ordenar: próximos primero, pasados/cerrados al final
  const eventosOrdenados = [...eventosFiltrados].sort((a, b) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaA = a.fecha_evento ? new Date(a.fecha_evento) : null;
    const fechaB = b.fecha_evento ? new Date(b.fecha_evento) : null;
    
    const aActivo = a.inscripciones_abiertas !== false && fechaA && fechaA >= hoy;
    const bActivo = b.inscripciones_abiertas !== false && fechaB && fechaB >= hoy;
    
    if (aActivo && !bActivo) return -1;
    if (!aActivo && bActivo) return 1;
    
    if (fechaA && fechaB) return fechaA - fechaB;
    if (fechaA && !fechaB) return -1;
    if (!fechaA && fechaB) return 1;
    
    return 0;
  });

  // Estadísticas
  const stats = {
    total: eventos.length,
    publicados: eventos.filter(e => e.estado === 'publicado').length,
    borradores: eventos.filter(e => e.estado === 'borrador').length,
    totalInscriptos: eventos.reduce((sum, e) => sum + (e.inscriptos_activos || 0), 0)
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-violet-600" />
            Gestión de Eventos
          </h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Crea y administra eventos con formularios personalizados</p>
        </div>
        <Button 
          onClick={() => navigate('/admin/eventos/nuevo')}
          className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Calendar className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total eventos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Send className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.publicados}</p>
                <p className="text-xs text-gray-500">Publicados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Edit className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.borradores}</p>
                <p className="text-xs text-gray-500">Borradores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalInscriptos}</p>
                <p className="text-xs text-gray-500">Inscriptos totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar eventos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {['todos', 'publicado', 'borrador', 'archivado'].map(estado => (
                <Button
                  key={estado}
                  variant={filtroEstado === estado ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroEstado(estado)}
                  className={filtroEstado === estado ? 'bg-violet-600' : ''}
                >
                  {estado.charAt(0).toUpperCase() + estado.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Eventos */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        </div>
      ) : eventosOrdenados.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay eventos que mostrar</p>
            <Button 
              onClick={() => navigate('/admin/eventos/nuevo')}
              className="mt-4 bg-violet-600 hover:bg-violet-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primer evento
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {eventosOrdenados.map(evento => (
            <Card key={evento.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Imagen */}
                  {evento.imagen_portada && (
                    <div className="w-full lg:w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={evento.imagen_portada} 
                        alt={evento.nombre}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: evento.imagen_focal_point 
                            ? `${evento.imagen_focal_point.x}% ${evento.imagen_focal_point.y}%`
                            : 'center'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Info principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-lg text-gray-900 truncate">{evento.nombre}</h3>
                      {getEstadoBadge(evento.estado)}
                      {evento.inscripciones_abiertas === false && (
                        <Badge className="bg-red-100 text-red-700">
                          <UserX className="w-3 h-3 mr-1" />
                          Inscripciones cerradas
                        </Badge>
                      )}
                      {evento.modalidad_admision === 'ticket_qr' && (
                        <Badge variant="outline" className="text-violet-600 border-violet-300">
                          <QrCode className="w-3 h-3 mr-1" />
                          QR
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {evento.fecha_evento && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(evento.fecha_evento)} {evento.hora_evento && `- ${evento.hora_evento}`}
                        </span>
                      )}
                      {evento.lugar && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {evento.lugar}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {evento.inscriptos_activos || 0} inscriptos
                        {evento.cupo_maximo && ` / ${evento.cupo_maximo}`}
                      </span>
                      {evento.configuracion_pago?.tipo !== 'gratis' && (
                        <span className="flex items-center gap-1 text-green-600">
                          <DollarSign className="w-4 h-4" />
                          {evento.inscriptos_pagados || 0} pagados
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 sm:flex-initial"
                      onClick={() => navigate(`/admin/eventos/${evento.id}/inscriptos`)}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      <span className="sm:inline">Inscriptos</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 sm:flex-initial"
                      onClick={() => navigate(`/admin/eventos/${evento.id}/editar`)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      <span className="sm:inline">Editar</span>
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {evento.estado === 'publicado' && (
                          <DropdownMenuItem onClick={() => window.open(`/eventos/${evento.id}`, '_blank')}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver público
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleDuplicar(evento.id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {evento.estado === 'borrador' && (
                          <DropdownMenuItem onClick={() => handleCambiarEstado(evento.id, 'publicado')}>
                            <Send className="w-4 h-4 mr-2" />
                            Publicar
                          </DropdownMenuItem>
                        )}
                        {evento.estado === 'publicado' && (
                          <>
                            <DropdownMenuItem onClick={() => handleToggleInscripciones(evento.id, evento.inscripciones_abiertas !== false)}>
                              {evento.inscripciones_abiertas !== false ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  Cerrar inscripciones
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Abrir inscripciones
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleCambiarEstado(evento.id, 'borrador')}>
                              <Edit className="w-4 h-4 mr-2" />
                              Despublicar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiarEstado(evento.id, 'archivado')}>
                              <Archive className="w-4 h-4 mr-2" />
                              Archivar
                            </DropdownMenuItem>
                          </>
                        )}
                        {evento.estado === 'archivado' && (
                          <DropdownMenuItem onClick={() => handleCambiarEstado(evento.id, 'borrador')}>
                            <Edit className="w-4 h-4 mr-2" />
                            Volver a borrador
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleEliminar(evento.id, evento.nombre)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
